<template>
  <v-alert
    v-if="needRefresh"
    variant="elevated"
    elevation="5"
    color=""
    position="fixed"
    location="bottom right"
    style="z-index: 9999;"
    class="pa-3 ma-3"
  >
    <h5 class="pt-3 pb-3 text-uppercase">{{ alertTitle }}</h5>
    <p v-if="hasBlockers" class="mb-3 text-body-2">
      Update will apply automatically when this screen is safe to reload.
    </p>
    <v-btn @click="queueUpdate()" class="w-100" color="primary" variant="elevated">
      Update Page
    </v-btn>
  </v-alert>
</template>
<script setup lang="ts">
import { useDeferredAppUpdate } from "@/composables/useDeferredAppUpdate";
import { useRegisterSW } from 'virtual:pwa-register/vue'
import { useRouter } from 'vue-router'

const {
  needRefresh,
  updateServiceWorker,
} = useRegisterSW()

const {
  hasBlockers,
  queueDeferredUpdate,
  tryApplyPendingUpdate,
} = useDeferredAppUpdate();

const alertTitle = computed(() =>
  hasBlockers.value
    ? "Update ready. Waiting for safe reload"
    : "There is an update available"
)

const doReload = () => {
  window.setTimeout(() => window.location.reload(), 500)
}

const queueUpdate = () => {
  queueDeferredUpdate(() => {
    updateServiceWorker(true)
    doReload()
  })
}

const router = useRouter()
let updating = false
router.afterEach(() => {
  if (needRefresh.value && !updating) {
    updating = true
    queueUpdate()
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        window.location.reload()
      },
      { once: true }
    )
  }
})

watch(needRefresh, value => {
  if (value && !updating) {
    updating = true
    queueUpdate()
  }
})

watch(hasBlockers, blocked => {
  if (!blocked && needRefresh.value) {
    void tryApplyPendingUpdate()
  }
})

let pendingCheckTimer: ReturnType<typeof setInterval> | null = null
let swUpdateCheckTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  pendingCheckTimer = setInterval(() => {
    if (needRefresh.value)
      void tryApplyPendingUpdate()
  }, 2000)

  swUpdateCheckTimer = setInterval(async () => {
    if (!('serviceWorker' in navigator))
      return

    const registration = await navigator.serviceWorker.getRegistration()
    if (registration)
      await registration.update()
  }, 60000)
})

onBeforeUnmount(() => {
  if (pendingCheckTimer) {
    clearInterval(pendingCheckTimer)
    pendingCheckTimer = null
  }

  if (!swUpdateCheckTimer)
    return

  clearInterval(swUpdateCheckTimer)
  swUpdateCheckTimer = null
})
</script>
